import React, { useMemo } from "react";

import Decimal from "decimal.js";
import { useHistory, useLocation } from "react-router-dom";

import useGetTradeProposalBundle from "@fartherfinance/frontend/api/Investment/hooks/useGetTradeProposalBundle";
import {
  TradeProposalBundleId,
  TradeProposalId,
} from "@fartherfinance/frontend/api/Investment/types";

import { BundleLocationState } from "../../types";
import Button from "@src/multiCustodian/components/MUI/Button/Button";
import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import Skeleton from "@src/sharedComponents/Skeleton/Skeleton";
import FullDataTable from "@src/sharedComponents/Table/FullDataTable";
import { Cell, Row } from "@src/sharedComponents/Table/Types";

type Columns =
  | "Proposal Id"
  | "Trade Count"
  | "Buy Count"
  | "Sell Count"
  | "Total Buys"
  | "Total Sells"
  | "Action";

const columns: NonEmptyArray<Columns> = [
  "Proposal Id",
  "Trade Count",
  "Buy Count",
  "Sell Count",
  "Total Buys",
  "Total Sells",
  "Action",
];

interface Props {
  tradeProposalBundleId: TradeProposalBundleId;
  removeProposal: (proposalId: TradeProposalId) => Promise<void>;
  isMutating: boolean;
  isInteractionDisabled: boolean;
}

const ProposalTable: React.FC<Props> = (props) => {
  const auth = useAdvisorRequestAuth();

  const history = useHistory<BundleLocationState>();
  const { state } = useLocation<BundleLocationState>();

  const bundle = useGetTradeProposalBundle(props.tradeProposalBundleId, auth);

  const rows = useMemo((): Row<Columns, Cell>[] | undefined => {
    if (bundle.data === undefined) {
      return undefined;
    }

    return bundle.data.tradeProposals.map((tradeProposal) => ({
      key: tradeProposal.proposalId,
      onClick: () => {
        const url = `/Advisor/FinancialOperations/TradeProposalBundles/${props.tradeProposalBundleId}/${tradeProposal.proposalId}`;
        history.push(url, state);
      },
      "Proposal Id": tradeProposal.proposalId,
      "Trade Count": tradeProposal.trades.length.toLocaleString("en-US"),
      "Total Buys": tradeProposal.trades
        .filter((t) => t.side === "Buy")
        .reduce((acc, t) => acc.plus(t.quantity.times(t.price)), new Decimal(0))
        .toNumber()
        .toLocaleString("en-US", { style: "currency", currency: "USD" }),
      "Total Sells": tradeProposal.trades
        .filter((t) => t.side === "Sell")
        .reduce((acc, t) => acc.plus(t.quantity.times(t.price)), new Decimal(0))
        .toNumber()
        .toLocaleString("en-US", { style: "currency", currency: "USD" }),
      "Buy Count": tradeProposal.trades
        .filter((t) => t.side === "Buy")
        .length.toLocaleString("en-US"),
      "Sell Count": tradeProposal.trades
        .filter((t) => t.side === "Sell")
        .length.toLocaleString("en-US"),
      Action: {
        hideUnlessHovering: true,
        value: (
          <Button
            text="Remove"
            variant="contained"
            buttonType="error"
            disabled={props.isMutating || props.isInteractionDisabled}
            onClick={async (e) => {
              // do not allow the row click to trigger
              e.stopPropagation();

              await props.removeProposal(tradeProposal.proposalId);
            }}
          />
        ),
      },
    }));
  }, [bundle.data, history, props, state]);

  return (
    <>
      {rows === undefined ? (
        <FullDataTable<Columns, Cell>
          key={"details-loading"}
          useQueryParams={false}
          columns={columns}
          defaultSortColumn={["Proposal Id", "asc"]}
          rows={Array.from({ length: 10 }).map((_, idx) => ({
            key: idx.toString(),
            "Proposal Id": { value: <Skeleton width={100} />, fullValue: "" },
            "Trade Count": { value: <Skeleton width={100} />, fullValue: "" },
            "Total Buys": { value: <Skeleton width={100} />, fullValue: "" },
            "Total Sells": { value: <Skeleton width={100} />, fullValue: "" },
            "Buy Count": { value: <Skeleton width={100} />, fullValue: "" },
            "Sell Count": { value: <Skeleton width={100} />, fullValue: "" },
            Action: { value: <Skeleton width={100} />, fullValue: "" },
          }))}
          emptyCell={<Skeleton />}
          hidePagination
          defaultRowsPerPage={10}
        />
      ) : (
        <FullDataTable<Columns, Cell>
          key={"details-loaded"}
          useQueryParams={false}
          columns={columns}
          defaultSortColumn={["Proposal Id", "asc"]}
          rows={rows}
          emptyCell={<Skeleton />}
          hidePagination
          defaultRowsPerPage={rows.length}
        />
      )}
    </>
  );
};

export default ProposalTable;
